import React from 'react';
import { ResearchState } from '../types/research';

interface ProgressIndicatorProps {
  state: ResearchState;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ state }) => {
  const phases = [
    { key: 'planning', label: 'Planning', icon: '🧠' },
    { key: 'searching', label: 'Searching', icon: '🔍' },
    { key: 'writing', label: 'Writing', icon: '✍️' },
    { key: 'completed', label: 'Completed', icon: '✅' }
  ];

  const getPhaseStatus = (phaseKey: string) => {
    if (state.currentPhase === 'error') return 'error';
    
    // Special case: when research is completed, show completed phase as completed, not current
    if (state.currentPhase === 'completed') {
      return 'completed';
    }
    
    if (state.currentPhase === phaseKey) {
      return 'current';
    }
    
    const currentIndex = phases.findIndex(p => p.key === state.currentPhase);
    const phaseIndex = phases.findIndex(p => p.key === phaseKey);
  
    
    if (phaseIndex < currentIndex) {
      return 'completed';
    }
    return 'pending';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500 text-white';
      case 'current': return 'bg-blue-500 text-white animate-pulse';
      case 'error': return 'bg-red-500 text-white';
      default: return 'bg-gray-200 text-gray-500';
    }
  };

  if (state.currentPhase === 'idle') {
    return null;
  }

  return (
    <div className="w-full max-w-4xl mx-auto mb-8">
      <div className="flex items-center justify-between">
        {phases.map((phase, index) => {
          const status = getPhaseStatus(phase.key);
          return (
            <React.Fragment key={phase.key}>
              <div className="flex flex-col items-center">
                <div
                  className={`
                    w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold
                    ${getStatusColor(status)}
                  `}
                >
                  {status === 'current' ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  ) : (
                    <span>{phase.icon}</span>
                  )}
                </div>
                <span className={`mt-2 text-sm font-medium ${
                  status === 'current' ? 'text-blue-600' : 
                  status === 'completed' ? 'text-green-600' :
                  status === 'error' ? 'text-red-600' : 'text-gray-500'
                }`}>
                  {phase.label}
                </span>
                {phase.key === 'searching' && state.currentPhase === 'searching' && (
                  <span className="text-xs text-gray-500 mt-1">
                    {state.searchesCompleted}/{state.searchesCount}
                  </span>
                )}
              </div>
              {index < phases.length - 1 && (
                <div className={`flex-1 h-1 mx-4 rounded ${
                  getPhaseStatus(phases[index + 1].key) === 'completed' ? 'bg-green-500' : 'bg-gray-200'
                }`}></div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      
      {state.currentPhase === 'error' && state.error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <span className="text-red-500 mr-2">⚠️</span>
            <span className="text-red-700 font-medium">Error:</span>
          </div>
          <p className="text-red-600 mt-1">{state.error}</p>
        </div>
      )}
    </div>
  );
};

export default ProgressIndicator; 